import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CashCurrentContextV1,
  ApprovedOpeningV1,
  CashOpenResponseV1,
  CashOperationLookupV1,
} from '@pokerroom/types';
import { api } from '../../../lib/api';
import { useAuthStore } from '../../../store/auth.store';
import { useCashIntent, useCashScope } from './useCashIntent';
import type { CashIntent } from './cashRecovery';
import { CashRecoveryNotice } from './CashRecoveryNotice';

type Catalog = {
  contractVersion: 1;
  sourceWatermark: string;
  businessDate: string;
  availability: string;
  items: ApprovedOpeningV1[];
};
export function CashOpeningPanel({
  context,
  onPending,
  blocked = false,
  recoveryOnly = false,
}: {
  context: CashCurrentContextV1;
  onPending?: (pending: boolean) => void;
  blocked?: boolean;
  recoveryOnly?: boolean;
}) {
  const { t } = useTranslation('accounting');
  const scope = useCashScope();
  const qc = useQueryClient();
  async function refreshCurrent() {
    await qc.refetchQueries(
      { queryKey: ['accounting-business-day'], type: 'active' },
      { throwOnError: true },
    );
    await qc.refetchQueries(
      { queryKey: ['accounting-active-session'], type: 'active' },
      { throwOnError: true },
    );
  }
  const cash = useCashIntent(scope, 'opening');
  useEffect(() => {
    onPending?.(!!cash.intent || cash.busy);
  }, [cash.intent, cash.busy, onPending]);
  const canOpen = useAuthStore((s) => !!s.allowedActions?.includes('accounting:open'));
  const canRead = useAuthStore((s) => !!s.allowedActions?.includes('accounting:read'));
  const [selected, setSelected] = useState(''),
    [manualKey, setManualKey] = useState('');
  const [receipt, setReceipt] = useState<CashOpenResponseV1 | null>(null);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    setCounts({});
    setConfirmed(false);
  }, [selected, context.currentBusinessDate]);
  const sources = useQuery({
    queryKey: ['cash-opening-sources', scope, context.sourceWatermark],
    enabled: canRead && !recoveryOnly,
    retry: false,
    queryFn: () => api.get<Catalog>('/accounting/opening-sources'),
  });
  const detail = useQuery({
    queryKey: ['cash-opening-source', scope, selected, context.sourceWatermark],
    enabled: canRead && !recoveryOnly && !!selected,
    retry: false,
    queryFn: () =>
      api.get<ApprovedOpeningV1 & Omit<Catalog, 'items' | 'availability'>>(
        `/accounting/opening-sources/${encodeURIComponent(selected)}`,
      ),
  });
  const eligible =
    detail.data &&
    detail.data.contractVersion === 1 &&
    detail.data.businessDate === context.currentBusinessDate &&
    detail.data.sourceWatermark === context.sourceWatermark &&
    detail.data.eligibility.canOpen &&
    !detail.data.consumedSessionId &&
    context.sessionState !== 'CURRENT_OPEN' &&
    context.guardReasons.every((r) => r === 'PRIOR_OPEN' && detail.data?.sourceKind === 'CARRY');
  const dailyBags =
    detail.data?.locations.filter((l) => ['ZIPA_DRAWER', 'RESERVE', 'PETTY'].includes(l.kind)) ??
    [];
  const countsValid =
    dailyBags.length === 3 &&
    dailyBags.every(
      (l) =>
        /^\d{1,10}(\.\d{1,2})?$/.test(counts[l.locationId] ?? '') &&
        Number(counts[l.locationId]) === Number(l.amount),
    );
  function validate(value: CashOpenResponseV1, record: CashIntent) {
    return (
      value?.contractVersion === 1 &&
      value.cashMode === 'LOCATION_V1' &&
      value.status === 'OPEN' &&
      value.openingRef === record.openingRef &&
      !!value.operationId &&
      !!value.sessionId &&
      !!value.businessDate &&
      !!value.sourceCutoff &&
      !!value.sourceWatermark &&
      !!value.asOf &&
      Array.isArray(value.locationOpenings) &&
      value.locationOpenings.every(
        (x) =>
          !!x.locationId &&
          typeof x.amount === 'string' &&
          (x.openingMovementId === null || typeof x.openingMovementId === 'string'),
      )
    );
  }
  async function submit(record?: CashIntent) {
    if (
      !canOpen ||
      (!record &&
        (recoveryOnly || !eligible || blocked || cash.intent || !countsValid || !confirmed))
    )
      return;
    const now = Date.now(),
      key = crypto.randomUUID();
    const r =
      record ??
      ({
        recordVersion: 1,
        scope,
        createdAt: now,
        lastSeenAt: now,
        action: 'OPEN_SESSION',
        operationKey: key,
        openingRef: selected,
        stage: 'UNKNOWN',
        body: {
          cashMode: 'LOCATION_V1',
          openingRef: selected,
          openingManifestHash: detail.data!.manifestHash,
          expectedSourceWatermark: detail.data!.sourceWatermark,
          operationKey: key,
          locationCounts: dailyBags.map((l) => ({
            locationId: l.locationId,
            amount: counts[l.locationId],
          })),
          countsConfirmed: confirmed,
        },
      } as CashIntent);
    const result = await cash.post<CashOpenResponseV1>(r, '/accounting/sessions/open', (value) =>
      validate(value, r),
    );
    if (result) {
      cash.committed(r);
      setReceipt(result);
      await cash.run(refreshCurrent);
    }
  }
  async function lookup() {
    if (!canRead) return;
    const key = cash.intent?.operationKey ?? manualKey.trim();
    if (!key) {
      cash.setError('RECONCILE');
      return;
    }
    const g = await cash.run(() =>
      api.get<CashOperationLookupV1>(`/accounting/operations/${encodeURIComponent(key)}`),
    );
    if (!g) return;
    if (
      g.contractVersion !== 1 ||
      g.action !== 'OPEN_SESSION' ||
      g.requestedOperationKey !== key ||
      g.result.operationId !== g.operationId ||
      g.result.replayed !== false
    ) {
      cash.setError('RECONCILE');
      return;
    }
    const now = Date.now();
    const r: CashIntent = cash.intent ?? {
      recordVersion: 1,
      scope,
      createdAt: now,
      lastSeenAt: now,
      action: 'OPEN_SESSION',
      operationKey: key,
      openingRef: g.result.openingRef,
      stage: 'COMMITTED',
    };
    if (!validate(g.result, r)) {
      cash.setError('RECONCILE');
      return;
    }
    cash.committed(r);
    setReceipt(g.result);
    await cash.run(refreshCurrent);
  }
  if (recoveryOnly && !cash.intent) return null;
  if (!canRead || cash.error === 'DENIED') return <CashRecoveryNotice error="DENIED" />;
  if (!recoveryOnly && (sources.isError || detail.isError))
    return (
      <p role="alert">
        {t('cashWorkflow.loadFailed')}{' '}
        <button
          className="btn min-h-11"
          onClick={() => {
            void sources.refetch();
            if (selected) void detail.refetch();
          }}
        >
          {t('cashWorkflow.refresh')}
        </button>
      </p>
    );
  return (
    <section
      className="break-words [overflow-wrap:anywhere] space-y-3 border border-base-300 rounded-lg p-4"
      aria-label={t('cash.opening')}
    >
      <h2>{t('cash.opening')}</h2>
      <p>
        {t('cash.context', {
          date: context.currentBusinessDate,
          state: t(`cash.${context.sessionState}`),
        })}
      </p>
      {sources.data?.availability === 'NO_APPROVED_SOURCE' && (
        <p role="status">{t('cash.noSource')}</p>
      )}
      {!recoveryOnly && !cash.intent && (
        <>
          <label>
            {t('cash.source')}
            <select
              aria-label={t('cash.source')}
              className="block min-h-11 w-full max-w-full rounded border border-base-300 bg-base-100 p-2"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">{t('cash.select')}</option>
              {sources.data?.items.map((x) => (
                <option
                  key={x.openingRef}
                  value={x.openingRef}
                  disabled={!x.eligibility.canOpen || !!x.consumedSessionId}
                >
                  {x.openingRef}（{x.totalCash}）
                </option>
              ))}
            </select>
          </label>
          {detail.data && (
            <div>
              <p>
                {t('cash.approval', {
                  amount: detail.data.totalCash,
                  date: detail.data.sourceBusinessDate,
                  ref: detail.data.approvalRef,
                })}
              </p>
              <p>{t('cash.cutoff', { date: detail.data.sourceCutoff })}</p>
              {detail.data.locations
                .filter((x) => !['ZIPA_DRAWER', 'RESERVE', 'PETTY'].includes(x.kind))
                .map((x) => (
                  <p key={x.locationId}>
                    {x.locationId}：{x.amount}
                  </p>
                ))}
              {dailyBags.map((bag) => (
                <label key={bag.locationId} className="block my-3">
                  {t(`cashWorkflow.bags.${bag.kind}`)} —{' '}
                  {t('cashWorkflow.book', { amount: bag.amount })}
                  <input
                    aria-label={t(`cashWorkflow.bags.${bag.kind}`)}
                    inputMode="decimal"
                    value={counts[bag.locationId] ?? ''}
                    onChange={(e) => {
                      setCounts((old) => ({ ...old, [bag.locationId]: e.target.value }));
                      setConfirmed(false);
                    }}
                    className="input input-bordered w-full"
                  />
                </label>
              ))}
              <p>{t('cashWorkflow.openCountsHelp')}</p>
              <label className="flex min-h-11 items-center gap-2">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                {t('cashWorkflow.confirmCounts')}
              </label>
            </div>
          )}
          <button
            className="min-h-11 rounded border border-base-300 px-3 py-2 disabled:opacity-50"
            disabled={blocked || !eligible || !canOpen || cash.busy || !countsValid || !confirmed}
            onClick={() => void submit()}
          >
            {t('cash.open')}
          </button>
        </>
      )}
      {receipt && (
        <p role="status">
          {t('cash.openReceipt', { session: receipt.sessionId, date: receipt.businessDate })}
        </p>
      )}
      {(receipt?.sessionId || context.sessionState === 'CURRENT_OPEN') && (
        <Link
          className="btn min-h-11"
          to={`/accounting/settle?sessionId=${encodeURIComponent(receipt?.sessionId ?? context.activeSessionId!)}`}
        >
          {t('cashWorkflow.title')}
        </Link>
      )}
      {cash.intent?.body && cash.intent.stage !== 'COMMITTED' && (
        <button
          className="min-h-11 rounded border border-base-300 px-3 py-2 disabled:opacity-50"
          disabled={!canOpen || cash.busy}
          onClick={() => void submit(cash.intent!)}
        >
          {t('cash.reopen')}
        </button>
      )}
      {!recoveryOnly && !cash.intent && (
        <label className="block">
          {t('cash.openKey')}
          <input
            className="block min-h-11 w-full rounded border border-base-300 bg-base-100 p-2"
            aria-label={t('cash.openKey')}
            value={manualKey}
            onChange={(e) => setManualKey(e.target.value)}
          />
        </label>
      )}
      <button
        className="min-h-11 rounded border border-base-300 px-3 py-2 disabled:opacity-50"
        disabled={cash.busy}
        onClick={() => void lookup()}
      >
        {t('cash.lookupOpen')}
      </button>
      <button
        className="min-h-11 rounded border border-base-300 px-3 py-2"
        disabled={cash.busy}
        onClick={() => void cash.run(refreshCurrent)}
      >
        {t('cash.refreshSnapshot')}
      </button>
      <CashRecoveryNotice error={cash.error} />
    </section>
  );
}
